import React from 'react'
import {
  User,
  Edit,
  ArrowRight,
  Mail,
  FileText,
  CheckSquare,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { LEAD_ACTIONS, buildActionPrompt, type LeadAction } from '../leadActions'
import type { LeadData } from '../services/notificationsApi'

const ICON_MAP: Record<string, LucideIcon> = {
  User,
  Edit,
  ArrowRight,
  Mail,
  FileText,
  CheckSquare,
  Zap,
}

interface LeadActionWelcomeProps {
  lead: LeadData
  onSelectAction: (promptText: string) => void
}

/**
 * Shown at the top of a new lead-contextualized chat instead of the generic WelcomeScreen.
 * Presents a controlled list of predefined lead actions.
 */
export const LeadActionWelcome: React.FC<LeadActionWelcomeProps> = ({ lead, onSelectAction }) => {
  const { currentTheme, isDarkMode } = useTheme()
  const enabledActions = LEAD_ACTIONS.filter((a) => a.enabled)
  const leadName = lead.name || lead.email || `Lead ${lead.id}`

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flex: 1,
        padding: '32px 24px 24px',
        overflowY: 'auto',
      }}
      className="custom-scrollbar"
    >
      {/* Lead identity header */}
      <div
        style={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
          border: `1px solid ${isDarkMode ? '#4F46E520' : '#C7D2FE'}`,
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          width: '100%',
          maxWidth: '480px',
          textAlign: 'center',
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
            fontSize: '18px',
            fontWeight: 700,
            color: '#FFFFFF',
          }}
        >
          {leadName.charAt(0).toUpperCase()}
        </div>

        <p style={{ fontSize: '11px', fontWeight: 600, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
          New Lead
        </p>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: currentTheme.text, margin: '0 0 4px' }}>
          {leadName}
        </h2>
        {lead.email && lead.name && (
          <p style={{ fontSize: '13px', color: currentTheme.secondrytext, margin: 0 }}>{lead.email}</p>
        )}
        {lead.company && (
          <p style={{ fontSize: '12px', color: currentTheme.mutedText, margin: '2px 0 0' }}>{lead.company}</p>
        )}
      </div>

      {/* Prompt */}
      <p
        style={{
          fontSize: '15px',
          fontWeight: 500,
          color: currentTheme.text,
          marginBottom: '16px',
          textAlign: 'center',
        }}
      >
        What would you like to do with this lead?
      </p>

      {/* Action grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '10px',
          width: '100%',
          maxWidth: '520px',
        }}
      >
        {enabledActions.map((action: LeadAction) => {
          const Icon = ICON_MAP[action.icon] ?? Zap
          return (
            <button
              key={action.id}
              id={`lead-action-${action.id}`}
              onClick={() => onSelectAction(buildActionPrompt(action, lead.name))}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px 14px',
                background: isDarkMode ? '#101012' : '#FFFFFF',
                border: `1px solid ${currentTheme.border}`,
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                boxShadow: currentTheme.cardShadow,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#4F46E5'
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(79,70,229,0.15)'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = currentTheme.border
                e.currentTarget.style.boxShadow = currentTheme.cardShadow
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '8px',
                  background: 'rgba(79,70,229,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              >
                <Icon size={15} color="#4F46E5" />
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: currentTheme.text, margin: '0 0 2px', lineHeight: 1.3 }}>
                  {action.label}
                </p>
                <p style={{ fontSize: '11px', color: currentTheme.mutedText, margin: 0, lineHeight: 1.4 }}>
                  {action.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      <p
        style={{
          fontSize: '12px',
          color: currentTheme.mutedText,
          marginTop: '20px',
          textAlign: 'center',
        }}
      >
        Or type a message below to ask anything about {leadName}.
      </p>
    </div>
  )
}
